import { defaultLocale } from "@/i18n/config";
import { parisTodayCalendarDay } from "@/lib/event-dates";

export function toDateInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function parseDateInput(value: string): Date {
  return new Date(`${value}T00:00:00`);
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

export function addMonths(date: Date, months: number): Date {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

export function compareDateInputs(a: string, b: string): number {
  return a.localeCompare(b);
}

export function normalizeDateRange(
  from: string | null,
  to: string | null,
): { from: string | null; to: string | null } {
  if (!from && !to) return { from: null, to: null };
  if (from && to && compareDateInputs(from, to) > 0) {
    return { from: to, to: from };
  }
  return { from, to };
}

export function formatDisplayDate(value: string): string {
  return new Intl.DateTimeFormat(defaultLocale, {
    dateStyle: "medium",
  }).format(parseDateInput(value));
}

export function formatShortDisplayDate(value: string): string {
  return new Intl.DateTimeFormat(defaultLocale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(parseDateInput(value));
}

export function formatMonthYear(year: number, month: number): string {
  return new Intl.DateTimeFormat(defaultLocale, {
    month: "long",
    year: "numeric",
  }).format(new Date(year, month, 1));
}

export type DateRangePresetId =
  | "all"
  | "today"
  | "last7Days"
  | "lastMonth"
  | "quarter"
  | "sixMonths"
  | "year";

export function getDateRangePreset(
  preset: DateRangePresetId,
): { from: string | null; to: string | null } {
  const todayInput = parisTodayCalendarDay();
  const today = parseDateInput(todayInput);

  switch (preset) {
    case "all":
      return { from: null, to: null };
    case "today":
      return { from: todayInput, to: todayInput };
    case "last7Days":
      return { from: toDateInput(addDays(today, -6)), to: todayInput };
    case "lastMonth": {
      const previousMonth = addMonths(today, -1);
      return {
        from: toDateInput(startOfMonth(previousMonth)),
        to: toDateInput(endOfMonth(previousMonth)),
      };
    }
    case "quarter": {
      const minDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const maxDate = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      return { from: toDateInput(minDate), to: toDateInput(maxDate) };
    }
    case "sixMonths":
      return { from: toDateInput(addMonths(today, -6)), to: todayInput };
    case "year":
      return { from: toDateInput(addMonths(today, -12)), to: todayInput };
  }
}

export type CalendarDay = {
  date: Date;
  dateInput: string;
  inCurrentMonth: boolean;
};

export function getCalendarMonthDays(year: number, month: number): CalendarDay[] {
  const firstOfMonth = new Date(year, month, 1);
  const startOffset = (firstOfMonth.getDay() + 6) % 7;
  const gridStart = addDays(firstOfMonth, -startOffset);
  const days: CalendarDay[] = [];

  for (let index = 0; index < 42; index += 1) {
    const date = addDays(gridStart, index);
    days.push({
      date,
      dateInput: toDateInput(date),
      inCurrentMonth: date.getMonth() === month,
    });
  }

  return days;
}

export function getOrderedRange(
  from: string | null,
  to: string | null,
): { start: string | null; end: string | null } {
  if (!from || !to) {
    return { start: from, end: to };
  }

  if (compareDateInputs(from, to) <= 0) {
    return { start: from, end: to };
  }

  return { start: to, end: from };
}

export function isDateInRange(
  dateInput: string,
  from: string | null,
  to: string | null,
): boolean {
  const { start, end } = getOrderedRange(from, to);
  if (!start || !end) return false;
  return dateInput >= start && dateInput <= end;
}

export function formatRangeSummary(
  from: string | null,
  to: string | null,
  allDatesLabel: string,
): string {
  if (!from && !to) return allDatesLabel;
  if (from && to) {
    if (from === to) return formatDisplayDate(from);
    return `${formatShortDisplayDate(from)} – ${formatShortDisplayDate(to)}`;
  }
  if (from) return `≥ ${formatShortDisplayDate(from)}`;
  if (to) return `≤ ${formatShortDisplayDate(to)}`;
  return allDatesLabel;
}

export type RangeSegment = "none" | "single" | "start" | "middle" | "end";

export function getRangeSegment(
  dateInput: string,
  from: string | null,
  to: string | null,
): RangeSegment {
  if (!from && !to) return "none";

  if (from && !to) {
    return dateInput === from ? "single" : "none";
  }

  if (!from && to) {
    return dateInput === to ? "single" : "none";
  }

  const { start, end } = getOrderedRange(from, to);
  if (!start || !end) return "none";
  if (start === end && dateInput === start) return "single";
  if (dateInput < start || dateInput > end) return "none";
  if (dateInput === start) return "start";
  if (dateInput === end) return "end";
  return "middle";
}
